import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useRef, useEffect, useState } from "react";
import api from "./lib/api";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ImpersonationBanner from "./components/ImpersonationBanner";
import TrainingTour from "./components/TrainingTour";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SidebarLabelsProvider } from "./contexts/SidebarLabelsContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import OpsDashboard from "./pages/OpsDashboard";
import Officers from "./pages/Officers";
import Clients from "./pages/Clients";
import Sites from "./pages/Sites";
import SitesMap from "./pages/SitesMap";
import Help from "./pages/Help";
import HelpPrint from "./pages/HelpPrint";
import Shifts from "./pages/Shifts";
import Attendance from "./pages/Attendance";
import Incidents from "./pages/Incidents";
import Maintenance from "./pages/Maintenance";
import TourScans from "./pages/TourScans";
import DAR from "./pages/DAR";
import SupervisorVisits from "./pages/SupervisorVisits";
import Availability from "./pages/Availability";
import VacationRequests from "./pages/VacationRequests";
import OpenShifts from "./pages/OpenShifts";
import Payroll from "./pages/Payroll";
import Tardies from "./pages/Tardies";
import StatusChecks from "./pages/StatusChecks";
import Breaks from "./pages/Breaks";
import AdhocApprovals from "./pages/AdhocApprovals";
import Geofence from "./pages/Geofence";
import LiveMap from "./pages/LiveMap";
import DispatchLog from "./pages/DispatchLog";
import Reports from "./pages/Reports";
import NoShowReport from "./pages/NoShowReport";
import AuditTrail from "./pages/AuditTrail";
import NotificationsLog from "./pages/NotificationsLog";
import NotificationConsole from "./pages/NotificationConsole";
import SecurityLog from "./pages/SecurityLog";
import DataRetention from "./pages/DataRetention";
import Profile from "./pages/Profile";
import { isClientTier } from "./lib/roles";
import Users from "./pages/Users";
import PortalLayout from "./components/PortalLayout";
import PortalDashboard from "./pages/PortalDashboard";
import PortalReportsPage from "./pages/PortalReportsPage";
import PortalSettings from "./pages/PortalSettings";
import ClientPortalSettings from "./pages/ClientPortalSettings";
import PortalActivity from "./pages/PortalActivity";
import AdminClientPortalConfig from "./pages/AdminClientPortalConfig";
import PortalTemplates from "./pages/PortalTemplates";
import SecurityDashboard from "./pages/SecurityDashboard";
import PortalComplaints from "./pages/PortalComplaints";
import AdminComplaints from "./pages/AdminComplaints";
import PendingApprovals from "./pages/PendingApprovals";
import Offices from "./pages/Offices";
import AdhocRequesters from "./pages/AdhocRequesters";
import SidebarLabels from "./pages/SidebarLabels";
import GuardPortal from "./pages/GuardPortal";
import AcceptInvite from "./pages/AcceptInvite";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Signup from "./pages/Signup";
import SuperConsole from "./pages/SuperConsole";
import AgencySettings from "./pages/AgencySettings";
import AccessDenied from "./pages/AccessDenied";
import { useScreenWakeLock } from "./hooks/useScreenWakeLock";
import { useBrowserFullscreen } from "./hooks/useBrowserFullscreen";
import { useIdleLogout } from "./hooks/useIdleLogout";

// Toaster styling is a stable literal — extracted to avoid creating a new
// object every render (which would defeat sonner's internal memoisation).
const TOAST_OPTIONS = {
  style: {
    background: "#121212",
    border: "1px solid #27272A",
    color: "#fff",
    borderRadius: 0,
    fontFamily: "DM Sans, sans-serif",
  },
};

const landingFor = (user) => {
  if (!user) return "/login";
  if (isClientTier(user)) return "/portal";
  if (user.role === "guard") return "/";
  return "/";
};

function Protected({ children, adminOnly, adminScope, superOnly, supAdminOnly, clientAllowed, guardAllowed, perm }) {
  const { user, loading, effectivePermissions, effectiveRole } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-zinc-500 font-mono text-xs uppercase tracking-widest">
        Authenticating session…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (superOnly && user.role !== "super_admin") return <Navigate to="/" replace />;
  // Client-tier portal users (client / client_admin / regional_manager / site_manager)
  // are locked to /portal unless the destination route explicitly opts in.
  if (isClientTier(user) && !clientAllowed) return <Navigate to="/portal" replace />;
  // Officers are locked to /-portal unless the destination route explicitly
  // opts in — e.g. /dar so officers can file their own Daily Activity Reports
  // via the clock-out deep-link.
  if (user.role === "officer" && !guardAllowed) return <Navigate to="/guard-portal" replace />;
  if (adminOnly && !(user.role === "admin" || user.role === "super_admin")) return <Navigate to="/" replace />;
  if (adminScope && !["admin", "super_admin", "chief_security_officer", "operations_manager"].includes(user.role)) return <Navigate to="/" replace />;
  if (supAdminOnly && !(user.role === "admin" || user.role === "supervisor" || user.role === "super_admin")) return <Navigate to="/" replace />;
  // iter278 — Fine-grained permission gate. Uses `effectiveRole` so an
  // admin previewing a narrower role gets the same AccessDenied their
  // audience would see. Bypass only for super_admin and the effective
  // admin role — matches the sidebar-filter logic in Layout.jsx so the
  // route gate never contradicts what the sidebar allowed through.
  const bypassPerm = effectiveRole === "super_admin" || effectiveRole === "admin";
  if (
    perm &&
    !bypassPerm &&
    Array.isArray(effectivePermissions) &&
    !effectivePermissions.includes(perm)
  ) {
    return <Layout><AccessDenied requiredPermission={perm} /></Layout>;
  }
  return <Layout>{children}</Layout>;
}

function PortalGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isClientTier(user)) return <Navigate to={landingFor(user)} replace />;
  return children;
}

function GuardGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "") return <Navigate to={landingFor(user)} replace />;
  return children;
}

function BareAuth({ children, adminOnly }) {
  const { user, loading } = useAuth();
  // iter281 — Keep the display awake on every fullscreen route (ops-center
  // TV pinned to /shifts/fullscreen, /tardies/fullscreen, etc.). Silent
  // on browsers that don't ship the Screen Wake Lock API.
  const wake = useScreenWakeLock(true);
  // iter304 — Native browser fullscreen so the URL bar / tab bar / bookmarks
  // bar disappear on ops-center TVs. Requires a user gesture (click), so we
  // expose a small pill-button in the corner; keypress `F` also toggles.
  const fs = useBrowserFullscreen();
  useEffect(() => {
    if (!fs.supported) return undefined;
    const onKey = (e) => {
      // `F` toggle. Skip when typing in inputs/textareas so ops filter
      // keystrokes don't accidentally exit fullscreen.
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        fs.toggle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fs]);
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return (
    <>
      {children}
      {/* Tiny corner cluster: fullscreen toggle + wake lock indicator.
          Fullscreen button intentionally rendered FIRST so it sits to
          the left of the wake-lock pill, and stays clickable even when
          the wake pill is `pointer-events-none`. Press `F` to toggle
          without touching the mouse. */}
      <div className="fixed bottom-3 right-3 z-50 flex items-center gap-2">
        {fs.supported && (
          <button
            type="button"
            onClick={fs.toggle}
            data-testid="fullscreen-toggle"
            title={fs.active
              ? "Exit fullscreen (Esc or F)"
              : "Enter fullscreen — hides browser URL / tab bar (F)"}
            className="pointer-events-auto flex items-center gap-1.5 px-2 py-1 border bg-[#0A0A0A]/80 hover:bg-[#0A0A0A] backdrop-blur-sm text-[10px] uppercase tracking-widest font-mono transition-colors"
            style={{
              borderColor: fs.active ? "#0A84FF" : "#3F3F46",
              color: fs.active ? "#7CC4FF" : "#a1a1aa",
            }}
          >
            <span
              className="size-1.5 rounded-full"
              style={{ background: fs.active ? "#0A84FF" : "#71717a" }}
            />
            {fs.active ? "Fullscreen" : "Fullscreen · F"}
          </button>
        )}
        <div
          data-testid="wake-lock-status"
          title={wake.error || (wake.active
            ? "Screen wake lock is active — display will stay on"
            : wake.supported
              ? "Wake lock pending — click anywhere to activate"
              : "Wake lock unsupported in this browser")}
          className="flex items-center gap-1.5 px-2 py-1 border bg-[#0A0A0A]/80 backdrop-blur-sm text-[10px] uppercase tracking-widest font-mono pointer-events-none"
          style={{
            borderColor: wake.active ? "#22C55E" : wake.supported ? "#FBBF24" : "#71717a",
            color: wake.active ? "#86EFAC" : wake.supported ? "#FBBF24" : "#a1a1aa",
          }}
        >
          <span
            className="size-1.5 rounded-full"
            style={{ background: wake.active ? "#22C55E" : wake.supported ? "#FBBF24" : "#71717a" }}
          />
          {wake.active ? "Screen on" : wake.supported ? "Waking…" : "No wake lock"}
        </div>
      </div>
    </>
  );
}

function IdleLogoutWatcher() {
  // Mounted once inside <BrowserRouter> so `useLocation()` can flag the
  // `/*/fullscreen` ops-center TV routes (which are exempt — those are
  // designed to run unattended for the full shift).
  //
  // iter294 — timeout is fetched from the tenant's operational settings
  // (`/agency/session-settings`) so each agency admin can pick their
  // own window (30 / 60 / 180 min, or 0 = never). Falls back to 180
  // min while the fetch is in flight.
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const warnToastRef = useRef(null);
  const [timeoutMinutes, setTimeoutMinutes] = useState(180);

  useEffect(() => {
    let cancelled = false;
    if (!user) return () => { cancelled = true; };
    (async () => {
      try {
        const res = await api.get("/agency/session-settings");
        if (!cancelled && typeof res.data?.idle_timeout_minutes === "number") {
          setTimeoutMinutes(res.data.idle_timeout_minutes);
        }
      } catch {
        // Endpoint not deployed yet or transient network — keep the
        // 180-min default. Never crash the app for a preference fetch.
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const enabled = !!user && timeoutMinutes > 0;

  useIdleLogout({
    timeoutMs: Math.max(timeoutMinutes, 5) * 60 * 1000,
    warnBeforeMs: 60 * 1000,
    enabled,
    onWarn: () => {
      warnToastRef.current = toast("Signing you out in 60 seconds…", {
        description: "No activity detected for a while. Move your mouse or press a key to stay signed in.",
        duration: 60 * 1000,
        important: true,
        style: { border: "1px solid #FF9F0A", color: "#FF9F0A" },
      });
    },
    onActivity: () => {
      if (warnToastRef.current) {
        try { toast.dismiss(warnToastRef.current); } catch { /* ignore */ }
        warnToastRef.current = null;
      }
    },
    onLogout: async () => {
      if (warnToastRef.current) {
        try { toast.dismiss(warnToastRef.current); } catch { /* ignore */ }
        warnToastRef.current = null;
      }
      try { await logout(); } catch { /* ignore */ }
      const minsLabel = timeoutMinutes >= 60
        ? `${(timeoutMinutes / 60).toFixed(timeoutMinutes % 60 === 0 ? 0 : 1)} hour${timeoutMinutes >= 120 ? "s" : ""}`
        : `${timeoutMinutes} minutes`;
      toast.info(`Signed out due to ${minsLabel} of inactivity.`, {
        description: "Sign back in to resume your session.",
      });
      navigate("/login", { replace: true });
    },
  });
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/super" element={<Protected superOnly><SuperConsole /></Protected>} />
      <Route path="/portal" element={<PortalGate><PortalLayout><PortalDashboard /></PortalLayout></PortalGate>} />
      <Route path="/portal/reports" element={<PortalGate><PortalLayout><PortalReportsPage kind="incidents" /></PortalLayout></PortalGate>} />
      <Route path="/portal/reports/incidents" element={<PortalGate><PortalLayout><PortalReportsPage kind="incidents" /></PortalLayout></PortalGate>} />
      <Route path="/portal/reports/maintenance" element={<PortalGate><PortalLayout><PortalReportsPage kind="maintenance" /></PortalLayout></PortalGate>} />
      <Route path="/portal/reports/dar" element={<PortalGate><PortalLayout><PortalReportsPage kind="dar" /></PortalLayout></PortalGate>} />
      <Route path="/portal/reports/tour-scans" element={<PortalGate><PortalLayout><PortalReportsPage kind="tour_scans" /></PortalLayout></PortalGate>} />
      <Route path="/portal/settings" element={<PortalGate><PortalSettings /></PortalGate>} />
      <Route path="/portal/complaints" element={<PortalGate><PortalLayout><PortalComplaints /></PortalLayout></PortalGate>} />
      <Route path="/guard-portal" element={<GuardGate><GuardPortal /></GuardGate>} />
      <Route path="/" element={<Protected guardAllowed perm="dashboard:view"><Dashboard /></Protected>} />
      <Route path="/ops-dashboard" element={<Protected perm="ops_dashboard:view"><OpsDashboard /></Protected>} />
      <Route path="/clients" element={<Protected perm="clients:view"><Clients /></Protected>} />
      <Route path="/clients/:clientId/portal-config" element={<Protected adminScope perm="settings:view"><AdminClientPortalConfig /></Protected>} />
      <Route path="/officers" element={<Protected perm="guards:view"><Officers /></Protected>} />
      {/* iter241 — /guards preserved as a permanent redirect so old bookmarks, deep links from emails, and mobile shortcuts keep resolving. */}
      <Route path="/guards" element={<Navigate replace to="/officers" />} />
      <Route path="/sites" element={<Protected perm="sites:view"><Sites /></Protected>} />
      <Route path="/sites/map" element={<Protected perm="sites:view"><SitesMap /></Protected>} />
      <Route path="/sites/map/fullscreen" element={<BareAuth><SitesMap /></BareAuth>} />
      <Route path="/shifts" element={<Protected perm="shifts:view"><Shifts /></Protected>} />
      <Route path="/shifts/fullscreen" element={<BareAuth><Shifts /></BareAuth>} />
      {/* iter323 — Training / Help center. Every signed-in user can
          reach any doc; the default tab is scoped by role. Print route
          is a bare page so browser print-to-PDF has no chrome. */}
      <Route path="/help" element={<Protected clientAllowed guardAllowed><Help /></Protected>} />
      <Route path="/help/print/:key" element={<BareAuth><HelpPrint /></BareAuth>} />
      <Route path="/attendance" element={<Protected perm="attendance:view"><Attendance /></Protected>} />
      <Route path="/incidents" element={<Protected perm="incidents:view"><Incidents /></Protected>} />
      <Route path="/maintenance" element={<Protected perm="reports:view"><Maintenance /></Protected>} />
      <Route path="/tour-scans" element={<Protected perm="tours:view"><TourScans /></Protected>} />
      <Route path="/incidents/fullscreen" element={<BareAuth><Incidents /></BareAuth>} />
      <Route path="/dar" element={<Protected guardAllowed perm="dar:view"><DAR /></Protected>} />
      <Route path="/dar/fullscreen" element={<BareAuth><DAR /></BareAuth>} />
      <Route path="/supervisor-visits" element={<Protected supAdminOnly perm="attendance:view"><SupervisorVisits /></Protected>} />
      <Route path="/supervisor-visits/fullscreen" element={<BareAuth><SupervisorVisits /></BareAuth>} />
      <Route path="/availability" element={<Protected guardAllowed perm="availability:view"><Availability /></Protected>} />
      <Route path="/availability/fullscreen" element={<BareAuth><Availability /></BareAuth>} />
      <Route path="/vacation-requests" element={<Protected guardAllowed perm="vacation_requests:view"><VacationRequests /></Protected>} />
      <Route path="/open-shifts" element={<Protected perm="shifts:view"><OpenShifts /></Protected>} />
      <Route path="/open-shifts/fullscreen" element={<BareAuth><OpenShifts /></BareAuth>} />
      <Route path="/payroll" element={<Protected perm="payroll:view"><Payroll /></Protected>} />
      <Route path="/tardies" element={<Protected perm="tardies:view"><Tardies /></Protected>} />
      <Route path="/tardies/fullscreen" element={<BareAuth><Tardies /></BareAuth>} />
      <Route path="/status-checks" element={<Protected perm="attendance:view"><StatusChecks /></Protected>} />
      <Route path="/breaks" element={<Protected perm="attendance:view"><Breaks /></Protected>} />
      <Route path="/adhoc-approvals" element={<Protected perm="adhoc_requesters:view"><AdhocApprovals /></Protected>} />
      <Route path="/geofence" element={<Protected perm="attendance:view"><Geofence /></Protected>} />
      <Route path="/live-map" element={<Protected perm="sites:view"><LiveMap /></Protected>} />
      <Route path="/live-map/fullscreen" element={<BareAuth><LiveMap /></BareAuth>} />
      <Route path="/dispatch/log" element={<Protected perm="shifts:view"><DispatchLog /></Protected>} />
      <Route path="/reports" element={<Protected clientAllowed perm="reports:view"><Reports /></Protected>} />
      <Route path="/reports/no-shows" element={<Protected perm="reports:view"><NoShowReport /></Protected>} />
      <Route path="/audit" element={<Protected adminOnly perm="audit_log:view"><AuditTrail /></Protected>} />
      <Route path="/notifications-log" element={<Protected adminOnly perm="settings:view"><NotificationsLog /></Protected>} />
      <Route path="/settings/notifications" element={<Protected adminScope perm="settings:view"><NotificationConsole /></Protected>} />
      <Route path="/security-log" element={<Protected adminOnly perm="security_log:view"><SecurityLog /></Protected>} />
      <Route path="/data-retention" element={<Protected adminScope perm="settings:view"><DataRetention /></Protected>} />
      <Route path="/settings/client-portal" element={<Protected adminScope perm="settings:view"><ClientPortalSettings /></Protected>} />
      <Route path="/settings/portal-templates" element={<Protected adminScope perm="settings:view"><PortalTemplates /></Protected>} />
      <Route path="/settings/security" element={<Protected adminScope perm="security_log:view"><SecurityDashboard /></Protected>} />
      <Route path="/portal-activity" element={<Protected adminScope perm="settings:view"><PortalActivity /></Protected>} />
      <Route path="/portal-complaints" element={<Protected adminScope perm="settings:view"><AdminComplaints /></Protected>} />
      <Route path="/pending-approvals" element={<Protected perm="shifts:edit"><PendingApprovals /></Protected>} />
      <Route path="/settings/offices" element={<Protected adminScope perm="offices:view"><Offices /></Protected>} />
      <Route path="/settings/adhoc-requesters" element={<Protected perm="adhoc_requesters:view"><AdhocRequesters /></Protected>} />
      <Route path="/settings/sidebar-labels" element={<Protected adminScope perm="settings:view"><SidebarLabels /></Protected>} />
      <Route path="/profile" element={<Protected clientAllowed><Profile /></Protected>} />
      <Route path="/users" element={<Protected adminScope perm="users:view"><Users /></Protected>} />
      <Route path="/agency-settings" element={<Protected adminScope perm="settings:view"><AgencySettings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <SidebarLabelsProvider>
          <BrowserRouter>
            <Toaster
              theme="dark"
              position="top-right"
              toastOptions={TOAST_OPTIONS}
            />
            <ImpersonationBanner />
            <IdleLogoutWatcher />
            <TrainingTour />
            <AppRoutes />
          </BrowserRouter>
        </SidebarLabelsProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
